import React, { useState } from "react";
import {
  initialNodeStyle,
  initialDirectedEdgeStyle,
  visitedNodeStyle,
  visitedDirectedEdgeStyle,
  visitingNodeStyle,
} from "./GraphStyle";
import "./DFSAlgorithm.css";

const SCC = ({ cytoscape, nodes }) => {
  const [visitedNodes, setVisitedNodes] = useState([]);
  const [allVisitedNodes, setAllVisitedNodes] = useState([]);
  const [dfsVisitedNodes, setDfsVisitedNodes] = useState([]);
  const [allVisitedNodesCopy, setAllVisitedNodesCopy] = useState([]);
  const [isAnimating, setIsAnimating] = useState(false);
  const [currentNode, setCurrentNode] = useState(null);
  const [stack, setStack] = useState([]);
  const [isTransposed, setIsTransposed] = useState(false);
  const [buttonLabel, setButtonLabel] = useState("Animate DFS");
  const [sccLists, setSccLists] = useState([]);
  const [isAnimateDFSCompleted, setIsAnimateDFSCompleted] = useState(false);
  const [
    isPerformDFSOnTransposedCompleted,
    setIsPerformDFSOnTransposedCompleted,
  ] = useState(false);

  const [removedEdgesData, setRemovedEdgesData] = useState([]);
  let visitedNodeCount = 0;
  let visitedNodesSet = new Set();

  const handleButtonClick = async () => {
    if (isAnimating) return;
    setIsAnimating(true);

    if (buttonLabel === "Animate DFS") {
      animateSCC();
      setButtonLabel("Transpose Graph");
    } else if (buttonLabel === "Transpose Graph") {
      transposeGraph(cytoscape);
      setButtonLabel("Perform DFS on Transposed Graph");
      setIsAnimateDFSCompleted(false);
    } else if (buttonLabel === "Perform DFS on Transposed Graph") {
      performDFSonTransposedGraph();
      setButtonLabel("Reset Animation");
      setIsPerformDFSOnTransposedCompleted(false);
    } else if (buttonLabel === "Reset Animation") {
      resetAnimation();
      setButtonLabel("Animate DFS");
    }
    setIsAnimating(false);
  };
  const dfs = async (startNode, parentNode = null, visitedNodesSet, stack) => {
    await new Promise((resolve) => setTimeout(resolve, 2000));
    const currentNode = cytoscape.getElementById(startNode.data.id);
    currentNode.style(visitingNodeStyle);

    let adjacentEdges = Array.from(
      cytoscape.edges(`[source="${startNode.data.id}"]`)
    );

    adjacentEdges.sort((edgeA, edgeB) => {
      const idA = edgeA.data("id");
      const idB = edgeB.data("id");
      return idA.localeCompare(idB);
    });

    for (const edge of adjacentEdges) {
      const sourceId = edge.source().id();
      const targetId = edge.target().id();
      const adjacentNodeId =
        sourceId === startNode.data.id ? targetId : sourceId;
      if (adjacentNodeId !== parentNode) {
        const adjacentNode = nodes.find(
          (node) => node.data.id === adjacentNodeId
        );
        if (!visitedNodesSet.has(adjacentNode.data.id)) {
          stack.push(adjacentNode.data.id);
          setStack([...stack]);
          visitedNodesSet.add(adjacentNode.data.id);
          setVisitedNodes([...visitedNodes, adjacentNode.data.id]);
          edge.style(visitedDirectedEdgeStyle);
          await dfs(adjacentNode, startNode.data.id, visitedNodesSet, stack);
        }
      }
    }

    await new Promise((resolve) => setTimeout(resolve, 2000));
    stack.pop(currentNode);
    setStack([...stack]);
    currentNode.style(visitedNodeStyle);
    setAllVisitedNodes((prevVisitedNodes) => [
      ...prevVisitedNodes,
      startNode.data.id,
    ]);
  };

  const transposeGraph = (cytoscape) => {
    if (!cytoscape) {
      console.error("Cytoscape instance is not available.");
      return;
    }

    // Check if the graph is already transposed
    if (isTransposed) {
      return;
    }

    // Store the removed edges
    const removedEdges = cytoscape.edges().map((edge) => edge.json());
    setRemovedEdgesData(removedEdges);
    const newEdgesData = [];

    // Remove existing edges
    cytoscape.edges().remove();
    cytoscape.nodes().style(initialNodeStyle);

    // Create new edges with reversed direction and updated IDs
    removedEdges.forEach((edgeData) => {
      const sourceId = parseInt(edgeData.data.source.split("node")[1]);
      const targetId = parseInt(edgeData.data.target.split("node")[1]);

      const reversedEdgeData = {
        data: {
          id: `edge${targetId}-${sourceId}`,
          source: `node${targetId}`,
          target: `node${sourceId}`,
        },
      };

      // Add the reversed edge data to the new edges data array
      newEdgesData.push(reversedEdgeData);
    });

    // Add the new reversed edges to the graph
    cytoscape.add(newEdgesData);

    setVisitedNodes([]);
    const allVisitedNodesCopy = [...allVisitedNodes];
    setAllVisitedNodesCopy(allVisitedNodesCopy);
    //setAllVisitedNodes([]);
    setStack([]);
    visitedNodesSet.clear();

    setIsAnimating(false);
    // Set the state to indicate that the graph is transposed
    setIsTransposed(true);
  };

  const animateSCC = async () => {
    if (isAnimating) return;
    setIsAnimating(true);

    const sortedNodes = [...nodes].sort((a, b) =>
      a.data.id.localeCompare(b.data.id)
    );

    for (const startNode of sortedNodes) {
      if (!visitedNodesSet.has(startNode.data.id)) {
        stack.push(startNode.data.id);
        setStack([...stack]);
        visitedNodesSet.add(startNode.data.id);
        setVisitedNodes([...visitedNodes, startNode.data.id]);
        await new Promise((resolve) => setTimeout(resolve, 1000));
        await dfs(startNode, visitedNodeCount, visitedNodesSet, stack);
      }
    }
    setIsAnimateDFSCompleted(true);
    setIsAnimating(false);
  };

  const scc = async (
    startNode,
    parentNode = null,
    visitedNodesSet,
    stack,
    currentSccList
  ) => {
    await new Promise((resolve) => setTimeout(resolve, 2000));
    const currentNode = cytoscape.getElementById(startNode.data.id);
    currentNode.style(visitingNodeStyle);

    let adjacentEdges = Array.from(
      cytoscape.edges(`[source="${startNode.data.id}"]`)
    );

    adjacentEdges.sort((edgeA, edgeB) => {
      const idA = edgeA.data("id");
      const idB = edgeB.data("id");
      return idA.localeCompare(idB);
    });

    for (const edge of adjacentEdges) {
      const sourceId = edge.source().id();
      const targetId = edge.target().id();
      const adjacentNodeId =
        sourceId === startNode.data.id ? targetId : sourceId;
      if (adjacentNodeId !== parentNode) {
        const adjacentNode = nodes.find(
          (node) => node.data.id === adjacentNodeId
        );
        if (!visitedNodesSet.has(adjacentNode.data.id)) {
          stack.push(adjacentNode.data.id);
          setStack([...stack]);
          visitedNodesSet.add(adjacentNode.data.id);
          setVisitedNodes([...visitedNodes, adjacentNode.data.id]);
          edge.style(visitedDirectedEdgeStyle);
          currentSccList.push(adjacentNode.data.id);
          await scc(
            adjacentNode,
            startNode.data.id,
            visitedNodesSet,
            stack,
            currentSccList
          );
        }
      }
    }

    await new Promise((resolve) => setTimeout(resolve, 2000));
    stack.pop(currentNode);
    setStack([...stack]);
    currentNode.style(visitedNodeStyle);
    setDfsVisitedNodes((prevVisitedNodes) => [
      ...prevVisitedNodes,
      startNode.data.id,
    ]);
  };

  const performDFSonTransposedGraph = async () => {
    if (isAnimating) return;
    setIsAnimating(true);
    setSccLists([]);
    const tempSccLists = [];
    for (const visitedNode of [...allVisitedNodesCopy].reverse()) {
      const startNode = nodes.find((node) => node.data.id === visitedNode);
      if (startNode && !visitedNodesSet.has(startNode.data.id)) {
        stack.push(startNode.data.id);
        setStack([...stack]);
        visitedNodesSet.add(startNode.data.id);
        setVisitedNodes([...visitedNodes, startNode.data.id]);
        const currentSccList = [startNode.data.id];
        await new Promise((resolve) => setTimeout(resolve, 1000));
        await scc(
          startNode,
          visitedNodeCount,
          visitedNodesSet,
          stack,
          currentSccList
        );
        tempSccLists.push(currentSccList);
        //setSccLists((prevSccLists) => [...prevSccLists, currentSccList]);
      }
    }
    setSccLists(tempSccLists);
    setIsPerformDFSOnTransposedCompleted(true);
    setIsAnimating(false);
  };

  const resetAnimation = () => {
    if (isAnimating) return;

    cytoscape.edges().remove();
    cytoscape.add(removedEdgesData);
    // Reset styles of all nodes and edges
    cytoscape.elements("node").style(initialNodeStyle);

    // Reset styles of all edges to initialEdgeStyleToApply
    cytoscape.elements("edge").style(initialDirectedEdgeStyle);

    // Reset the state
    setSccLists([]);
    setVisitedNodes([]);
    setAllVisitedNodes([]);
    setDfsVisitedNodes([]);
    setStack([]);
    visitedNodesSet.clear();

    setIsAnimating(false);
    setIsTransposed(false);
  };

  return (
    <div className="scc-container">
      <div className="scc-buttons">
        <button
          className="generate-reset-button"
          onClick={handleButtonClick}
          disabled={
            isAnimating ||
            (!isAnimateDFSCompleted && buttonLabel === "Transpose Graph") ||
            (!isPerformDFSOnTransposedCompleted &&
              buttonLabel === "Reset Animation")
          }
        >
          {isAnimating ? "Animating..." : buttonLabel}
        </button>
        &nbsp;&nbsp;
        {/*<button
          className="generate-reset-button"
          onClick={resetAnimation}
          disabled={
            isAnimating ||
            (!isAnimateDFSCompleted && buttonLabel === "Transpose Graph") ||
            (!isPerformDFSOnTransposedCompleted && buttonLabel === "Start over")
          }
        >
          Reset Animation
        </button>*/}
      </div>
      <div>
        <div className="stack-container">
          <div className="stack-title">Stack:</div> &nbsp;
          <ul className="stack">
            {[...stack].reverse().map((item, index) => (
              <li key={index}>{item}</li>
            ))}
          </ul>
        </div>
      </div>
      <div>
        <div className="stack-container">
          <div className="stack-title">Decreasing u.f order:</div>
          <ul className="stack">
            {[...allVisitedNodes].reverse().map((visitedNode, index) => (
              <li key={index}>{visitedNode}</li>
            ))}
          </ul>
        </div>
        <div className="stack-container">
          <div className="stack-title">DFS on transposed graph:</div>
          <ul className="stack">
            {[...dfsVisitedNodes].reverse().map((visitedNode, index) => (
              <li key={index}>{visitedNode}</li>
            ))}
          </ul>
        </div>
        <div className="stack-container">
          {/*<div className="stack-title">SCCs:</div>*/}
          {sccLists.map((scc, index) => (
            <div className="scc-item" key={index}>
              <span className="scc-number">SCC {index + 1}:</span>
              <span className="scc-node-list">{scc.join(", ")}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SCC;
